import React from 'react';
import './App.css';
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";
import './locales/i18n'; // Import your i18n configuration
import CssBaseline from '@mui/material/CssBaseline';
import ObossTheme from './theme/ObossTheme';
import NavigationAppBar from './components/NavigationAppBar';
import i18n from './locales/i18n';
import { cookies } from './cookies';
import { AppBarNavigationProps, HomePageProps, PageProps } from './types/navigationProperties';
import { displayErrorToast } from './utils/toasts';
import { getAllProjects, Project } from './services/ProjectsServiceProvider';
import { getAllCompanies, Company } from './services/CompaniesServiceProvider';
import { getAllMetaTags, MetaTag } from './services/SEOServiceProvider';
import { PaginationRequest, PaginatedResponse } from './types/pagination';
import { ToastContainer } from 'react-toastify';
import { Routes, Route } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import LoaderSpinner from './components/LoaderSpinner';
import Home from './pages/home';
import CookiesConsent from './components/CookiesConsent';
import GuitarServiceList from './pages/gallery/guitarService';
import GuitarServiceDetails from './pages/gallery/guitarService/details';
import CustomShopList from './pages/gallery/customShop';
import CustomShopDetails from './pages/gallery/customShop/details';
import PresentationList from './pages/presentation';
import ProjectDetails from './pages/project/details';
import AboutUsDetails from './pages/about';
import ContactUsDetails from './pages/contact';
import TermsAndConditions from './pages/terms';
import PrivacyPolicy from './pages/privacy';

function App(props: { disableCustomTheme?: boolean }) {
    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
        setLanguage(i18n.language);
        cookies.set('language', i18n.language, { path: '/' }); //set cookie
        // console.log('language changed');
        // console.log(lng);
        // window.location.href = "/";
    };

    const [language, setLanguage] = React.useState(cookies.get('language') || i18n.language);
    const [projects, setProjects] = React.useState<PaginatedResponse<Project>>();
    const [companies, setCompanies] = React.useState<PaginatedResponse<Company>>();
    const [metaTags, setMetaTags] = React.useState<MetaTag[]>();
    const [pageName, setPageName] = React.useState<string>('home');
    const [loading, setLoading] = React.useState(true);
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.up('md')); // 'md' is Material-UI's tablets breakpoint
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg')); // 'lg' is Material-UI's large breakpoint
    const [imageSize, setImageSize] = React.useState<string>(isLargeScreen ? 'lg' : isTablet ? 'md' : 'xs');

    const navProps: AppBarNavigationProps = {language: language, changeLanguage: changeLanguage, projects: projects, loading: loading};
    const homeProps: HomePageProps = {language: language, changeLanguage: changeLanguage, projects: projects, loading: loading, companies: companies};
    const pageProps: PageProps = {language: language, companies: companies};

    React.useEffect(() => {
        if(isLargeScreen) {
            setImageSize('lg');
        } else if(isTablet) {
            setImageSize('md');
        } else {
            setImageSize('xs');
        }
        // console.log('image size: ' + imageSize );
        const initialProjectsPagination: PaginationRequest = {page: 0, page_size: 5}
        i18n.changeLanguage(cookies.get('language') || i18n.language);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (async () => {
            try {
                setLoading(true);
                const metaTagsData = await getAllMetaTags(initialProjectsPagination);
                const projectsData = await getAllProjects(initialProjectsPagination, imageSize);
                const companiesData = await getAllCompanies(initialProjectsPagination);
                setProjects(projectsData);
                setCompanies(companiesData);
                setMetaTags(metaTagsData);
            } catch (error) {
                displayErrorToast('Failed to load application data!');
            } finally {
                setLoading(false);
            }
        })();
    }, [language, isLargeScreen, isTablet, imageSize]);

    return metaTags === undefined ? (
        <LoaderSpinner loading={loading} />
    ) : (
            <ObossTheme {...props}>
                <CookiesConsent />
                <CssBaseline enableColorScheme />
                <NavigationAppBar props={navProps} pageName={pageName} />
                <ToastContainer />
                <Routes>
                    <Route path="/" element={ <Home props={homeProps} setPageName={setPageName} metaTags={metaTags} /> } />
                    <Route path="/guitar-service" element= { <GuitarServiceList props={pageProps} setPageName={setPageName} imageSize={imageSize} metaTags={metaTags} /> } />
                    <Route path="/guitar-service/:slug" element= { <GuitarServiceDetails props={pageProps} setPageName={setPageName} imageSize={imageSize} metaTags={metaTags} /> } />
                    <Route path="/custom-shop" element= { <CustomShopList props={pageProps} setPageName={setPageName} imageSize={imageSize} metaTags={metaTags} /> }  />
                    <Route path="/custom-shop/:slug" element= { <CustomShopDetails props={pageProps} setPageName={setPageName} imageSize={imageSize} metaTags={metaTags} /> } />
                    <Route path="/video-presentation" element= { <PresentationList props={pageProps} setPageName={setPageName} metaTags={metaTags} /> } />
                    <Route path="/projects/:slug" element= { <ProjectDetails props={pageProps} setPageName={setPageName} imageSize={imageSize} metaTags={metaTags} /> } />
                    <Route path="/about-us" element= { <AboutUsDetails props={pageProps} setPageName={setPageName} metaTags={metaTags} /> } />
                    <Route path="/contact-us" element= { <ContactUsDetails props={pageProps} setPageName={setPageName} metaTags={metaTags} /> } />
                    <Route path="/terms-and-conditions" element= { <TermsAndConditions props={pageProps} setPageName={setPageName} metaTags={metaTags} /> } />
                    <Route path="/privacy-policy" element= { <PrivacyPolicy props={pageProps} setPageName={setPageName} metaTags={metaTags} /> } />
                </Routes>
            </ObossTheme>
    );
}

export default App;
